import * as generated from './generated/progress-validators.js';
import type { StandaloneValidate } from './generated/progress-validators.js';
import { emptyProblemDrafts, validateProblemDrafts } from './problem-drafts';
import type { Drafts, ExportFile, LessonProgress, ProgressState, Settings } from './types';

/**
 * 進捗エクスポートの schema 検証（progress-store.md §6.2 手順 5）。
 * 本番 CSP では実行時に Ajv で compile できない（new Function）ため、
 * scripts/generate-validators.ts がビルド前に生成した検証関数（Ajv standalone）を使う。
 * format（date-time）は検査しない（validateFormats: false。ajv-formats は足さない）。
 */

export type SchemaCheck<T> =
  { ok: true; value: T } | { ok: false; instancePath: string; message: string };

function check<T>(v: StandaloneValidate, data: unknown): SchemaCheck<T> {
  const isT = (d: unknown): d is T => v(d);
  if (isT(data)) return { ok: true, value: data };
  const first = v.errors?.[0];
  return {
    ok: false,
    instancePath: first?.instancePath || '/',
    message: first?.message ?? 'invalid',
  };
}

/**
 * エクスポートファイルの検証。problemDrafts（任意）は schema の外なので取り出して別に検証し、
 * 残りを生成した検証関数に通す（progress-store.md §9）。無ければ空の problemDrafts を補う。
 */
export function validateExportFile(d: unknown): SchemaCheck<ExportFile> {
  if (typeof d !== 'object' || d === null || !('problemDrafts' in d)) {
    const r = check<ExportFile>(generated.validateExport, d);
    return r.ok ? { ok: true, value: { ...r.value, problemDrafts: emptyProblemDrafts() } } : r;
  }
  const { problemDrafts, ...rest } = d as Record<string, unknown>;
  const pd = validateProblemDrafts(problemDrafts);
  if (!pd.ok) {
    return {
      ok: false,
      instancePath: `/problemDrafts${pd.instancePath === '/' ? '' : pd.instancePath}`,
      message: pd.message,
    };
  }
  const r = check<ExportFile>(generated.validateExport, rest);
  return r.ok ? { ok: true, value: { ...r.value, problemDrafts: pd.value } } : r;
}
export const validateProgress = (d: unknown): SchemaCheck<ProgressState> =>
  check(generated.validateProgress, d);
export const validateSettings = (d: unknown): SchemaCheck<Settings> =>
  check(generated.validateSettings, d);
export const validateDrafts = (d: unknown): SchemaCheck<Drafts> =>
  check(generated.validateDrafts, d);
export const validateLessonProgress = (d: unknown): SchemaCheck<LessonProgress> =>
  check(generated.validateLessonProgress, d);
